import { Case, Switch } from "#root/components/conditionals";
import * as Fields from "#root/components/form/fields";
import StandardEditor, { type StandardEditorProps } from "#root/components/standard-editor";
import { accountCollection, type AccountDetailItemSchema, type AccountListItem, type AccountListItemSchema } from "#root/schema/account";
import type { RecordModificationResponse } from "#root/utility/remote-data";
import type { ReactNode } from "react";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "./layout";

export default function ({ accountId }): ReactNode {
  const navigate = useNavigate();
  const handleUpdate = useCallback(
    (data: RecordModificationResponse<AccountListItem>) => {
      if ("deleted" in data) {
        navigate("/admin/account");
      } else if ("created" in data) {
        navigate(`/admin/account/${data.created}`);
      }
    },
    [navigate],
  );

  return (
    <PageLayout title={`Admin - Edit Account ${accountId}`}>
      <section>
        <Switch>
          <Case expr={accountId === "new"}>
            <h1>New Account</h1>
            <AccountEditor collection={accountCollection} onSave={handleUpdate}/>
          </Case>
          <Case expr={accountId !== null}>
            <h1>Edit Account - Account {accountId}</h1>
            <AccountEditor id={accountId} collection={accountCollection} onSave={handleUpdate} onDelete={handleUpdate}/>
          </Case>
        </Switch>
      </section>
    </PageLayout>
  );
}

function AccountEditor(props: StandardEditorProps<typeof AccountListItemSchema, typeof AccountDetailItemSchema>) {
  return (
    <StandardEditor {...props}>
      <Fields.Set name="type">
        <label>Account Type</label>
        <Fields.Select>
          <Fields.Option value="individual">Individual</Fields.Option>
          <Fields.Option value="corporation">Corporation</Fields.Option>
        </Fields.Select>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="name">
        <label>Account Name</label>
        <Fields.FullName required/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="website">
        <label>Website</label>
        <Fields.WebsiteURI/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="lei">
        <label>Legal Entity Name</label>
        <Fields.FullName/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="taxId">
        <label>Tax ID</label>
        <Fields.TaxID/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="billingAddress">
        <label>Billing Address</label>
        <Fields.TextArea rows={5}/>
        <Fields.Validity/>
      </Fields.Set>
    </StandardEditor>
  );
}
